import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { FileBarChart, Download, Building2, ShoppingCart, Wallet, Users, ShieldCheck, Siren, Lock, ArrowRight, CalendarClock } from 'lucide-react';
import DailyAttendanceReport from './reports/DailyAttendanceReport';
import { useAuth } from '../contexts/AuthContext';

const STATIC_REPORTS = [
  { cat: 'Project', icon: Building2, items: ['Project Progress Summary','Project Cost Report','S-Curve Report','Milestone Status','Resource Utilisation'] },
  { cat: 'Procurement', icon: ShoppingCart, items: ['PO Summary','Vendor Performance','Pending POs','Material Consumption','Stock Position'] },
  { cat: 'Finance', icon: Wallet, items: ['Project P&L','Cash Flow','AP Aging','AR Aging','Budget Variance','GST Report','TDS Report'] },
  { cat: 'HR', icon: Users, items: ['Manpower On Roll','Payroll Register','Labour Cost'] },
  { cat: 'HSE', icon: Siren, items: ['Monthly HSE Performance','Incident Summary','Training Compliance'] },
  { cat: 'Quality', icon: ShieldCheck, items: ['NCR Status','Snag List','Test Result Summary'] },
];

function ReportsIndex() {
  const { user } = useAuth() || {};
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isExec = role === 'super_admin' || role === 'md' || role === 'ceo' || role === 'cmd' || ['MD', 'CEO', 'CMD'].includes(dept);
  const canViewAttendance = isExec || role === 'cfo' || role === 'hr' || ['CFO', 'HR'].includes(dept);

  return (
    <div>
      <PageHeader overline="Reports & MIS" title="Business Intelligence" subtitle="Pre-built reports across all modules — exportable to Excel and PDF." />

      {/* Live / wired reports — actually downloadable */}
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-emerald-700 mb-2">⚡ Live Reports</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/app/reports/daily-attendance"
            data-testid="report-daily-attendance"
            className={`bg-white border-2 rounded-sm p-5 transition-all relative ${canViewAttendance ? 'border-[#1F7A6E] hover:shadow-lg cursor-pointer' : 'border-slate-200 opacity-60 cursor-not-allowed'}`}
            onClick={(e) => { if (!canViewAttendance) e.preventDefault(); }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-sm bg-emerald-600 text-white flex items-center justify-center">
                <CalendarClock size={17} />
              </div>
              {!canViewAttendance && (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-700 font-display font-bold">
                  <Lock size={11} /> Restricted
                </span>
              )}
            </div>
            <div className="kpi-overline">Auto · 20:00 IST</div>
            <h3 className="font-display font-bold text-base mt-1">Daily Attendance Report</h3>
            <p className="text-xs text-slate-600 mt-1">Project-wise headcount snapshot · Auto-emailed to MD / CEO / CMD / CFO / HR / Super Admin daily.</p>
            {canViewAttendance && (
              <div className="mt-3 text-xs text-[#1F7A6E] font-semibold flex items-center gap-1">
                Open report <ArrowRight size={12} />
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Static catalogue — placeholders for now */}
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Coming Soon · catalogue</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STATIC_REPORTS.map((r) => (
            <div key={r.cat} className="bg-white border border-slate-200 rounded-sm">
              <div className="p-5 border-b border-slate-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-teal-50 text-[#1F7A6E] flex items-center justify-center">
                  <r.icon size={17} />
                </div>
                <div>
                  <div className="kpi-overline">{r.cat}</div>
                  <h3 className="font-display font-bold text-base mt-0.5">{r.cat} Reports</h3>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {r.items.map(i => (
                  <button
                    key={i}
                    data-testid={`report-${i.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={() => alert(`"${i}" — coming soon. Click "Work Order MIS Report" above for the first live report.`)}
                    className="w-full text-left px-5 py-3 hover:bg-teal-50/40 flex items-center justify-between text-sm group"
                  >
                    <span className="flex items-center gap-2 text-slate-700"><FileBarChart size={13} className="text-slate-400" />{i}</span>
                    <Download size={13} className="text-slate-400 group-hover:text-[#1F7A6E]" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <Routes>
      <Route index element={<ReportsIndex />} />
      <Route path="daily-attendance" element={<DailyAttendanceReport />} />
    </Routes>
  );
}
